import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import {
  ArrowLeft,
  MapPin,
  Wrench,
  Sparkles,
  Truck,
  MoreHorizontal,
  Loader2,
  DollarSign
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fix Leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Map click handler component
const LocationPicker = ({ onLocationSelect, position }) => {
  useMapEvents({
    click(e) {
      onLocationSelect(e.latlng);
    }
  });

  return position ? <Marker position={[position.lat, position.lng]} /> : null;
};

const categories = [
  { value: 'plumbing', icon: Wrench, color: 'bg-blue-500' },
  { value: 'cleaning', icon: Sparkles, color: 'bg-emerald-500' },
  { value: 'delivery', icon: Truck, color: 'bg-amber-500' },
  { value: 'others', icon: MoreHorizontal, color: 'bg-purple-500' }
];

const paymentMethods = ['cash', 'zelle', 'paypal', 'crypto'];

export default function CreateJob() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, token } = useAuth();

  const [formData, setFormData] = useState({
    title: '',
    category: '',
    description: '',
    budget: '',
    location: null,
    locationAddress: '',
    urgent: false,
    payment_methods: ['cash']
  });
  const [loading, setLoading] = useState(false);
  const [mapCenter, setMapCenter] = useState([40.7128, -74.0060]);

  // Get user location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setMapCenter([pos.coords.latitude, pos.coords.longitude]);
        },
        () => {
          // Use default
        }
      );
    }
  }, []);

  // Redirect if not employer
  useEffect(() => {
    if (user && user.role !== 'employer') {
      toast.error('Only employers can post jobs');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleLocationSelect = useCallback(async (latlng) => {
    setFormData(prev => ({
      ...prev,
      location: { lat: latlng.lat, lng: latlng.lng }
    }));

    // Reverse geocode to get address
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latlng.lat}&lon=${latlng.lng}`
      );
      const data = await response.json();
      if (data.display_name) {
        setFormData(prev => ({
          ...prev,
          locationAddress: data.display_name
        }));
      }
    } catch (error) {
      console.error('Geocoding error:', error);
    }
  }, []);

  const handlePaymentMethodToggle = (method) => {
    setFormData(prev => ({
      ...prev,
      payment_methods: prev.payment_methods.includes(method)
        ? prev.payment_methods.filter(m => m !== method)
        : [...prev.payment_methods, method]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.category || !formData.description || !formData.budget) {
      toast.error('Please fill all required fields');
      return;
    }

    if (!formData.location) {
      toast.error('Please select a location on the map');
      return;
    }

    if (formData.payment_methods.length === 0) {
      toast.error('Please select at least one payment method');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/jobs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: formData.title,
          category: formData.category,
          description: formData.description,
          budget: parseFloat(formData.budget),
          location: {
            lat: formData.location.lat,
            lng: formData.location.lng,
            address: formData.locationAddress
          },
          urgent: formData.urgent,
          payment_methods: formData.payment_methods
        }),
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to create job');

      const job = await response.json();
      toast.success(t('common.success'));
      navigate(`/jobs/${job.job_id}`);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24 md:pb-8">
      <Navbar />

      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>{t('common.back')}</span>
        </button>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
          <div className="p-6 border-b border-slate-100">
            <h1 className="text-2xl font-bold text-slate-900">{t('job_form.title')}</h1>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">{t('job_form.job_title')} *</Label>
              <Input
                id="title"
                data-testid="job-title-input"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder={t('job_form.job_title_placeholder')}
                required
                className="py-5"
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label>{t('job_form.category')} *</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {categories.map((cat) => (
                  <button
                    key={cat.value}
                    type="button"
                    data-testid={`category-${cat.value}-select`}
                    onClick={() => setFormData(prev => ({ ...prev, category: cat.value }))}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                      formData.category === cat.value
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className={`w-12 h-12 ${cat.color} rounded-xl flex items-center justify-center`}>
                      <cat.icon className="w-6 h-6 text-white" />
                    </div>
                    <span className={`font-medium text-sm ${formData.category === cat.value ? 'text-emerald-700' : 'text-slate-700'}`}>
                      {t(`landing.${cat.value}`)}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">{t('job_form.description')} *</Label>
              <Textarea
                id="description"
                data-testid="job-description-input"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder={t('job_form.description_placeholder')}
                required
                rows={4}
                className="resize-none"
              />
            </div>

            {/* Budget */}
            <div className="space-y-2">
              <Label htmlFor="budget">{t('job_form.budget')} *</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="budget"
                  data-testid="job-budget-input"
                  type="number"
                  min="1"
                  step="0.01"
                  value={formData.budget}
                  onChange={(e) => setFormData(prev => ({ ...prev, budget: e.target.value }))}
                  placeholder="100"
                  required
                  className="pl-10 py-5"
                />
              </div>
            </div>

            {/* Location */}
            <div className="space-y-2">
              <Label>{t('job_form.location')} *</Label>
              <p className="text-sm text-slate-500">{t('job_form.location_placeholder')}</p>
              
              {formData.locationAddress && (
                <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg text-emerald-700">
                  <MapPin className="w-5 h-5 shrink-0" />
                  <span className="text-sm">{formData.locationAddress}</span>
                </div>
              )}

              <div className="h-64 rounded-xl overflow-hidden border border-slate-200">
                <MapContainer
                  center={mapCenter}
                  zoom={12}
                  className="h-full w-full"
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <LocationPicker 
                    onLocationSelect={handleLocationSelect} 
                    position={formData.location}
                  />
                </MapContainer>
              </div>
            </div>

            {/* Urgent */}
            <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-xl">
              <Checkbox
                id="urgent"
                data-testid="job-urgent-checkbox"
                checked={formData.urgent}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, urgent: checked }))}
              />
              <Label htmlFor="urgent" className="text-amber-800 cursor-pointer">
                {t('job_form.urgent_label')}
              </Label>
            </div>

            {/* Payment Methods */}
            <div className="space-y-3">
              <Label>{t('job_form.payment_methods')} *</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {paymentMethods.map((method) => (
                  <button
                    key={method}
                    type="button"
                    data-testid={`payment-${method}-btn`}
                    onClick={() => handlePaymentMethodToggle(method)}
                    className={`p-3 rounded-xl border-2 font-medium capitalize transition-all ${
                      formData.payment_methods.includes(method)
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    {t(`job_form.${method}`)}
                  </button>
                ))}
              </div>
            </div>

            {/* Submit */}
            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                className="flex-1 py-5"
              >
                {t('job_form.cancel')}
              </Button>
              <Button
                type="submit"
                data-testid="submit-job-btn"
                disabled={loading}
                className="flex-1 py-5 bg-emerald-500 hover:bg-emerald-600 text-white"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : null}
                {t('job_form.submit')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
